// Phase 10 — AdminDashboardPage.
//
// Route-level wrapper for the Admin/Founder Dashboard. P19 S1:
// refresh the real-data snapshots on mount (admin-gated RPC; the
// AdminOnly route guard already ensures only the founder reaches
// here, and the RPC itself raises for non-admins). Widgets without a
// real source stay absent and render "—" — never fabricated.
//
// DASH-HARDEN-1 (2026-05-20): refresh errors used to be swallowed in
// a `catch {/* non-fatal */}` block. Today's audit caught a case
// where the snapshots table was literally empty (0 rows total) but
// nobody could tell whether the refresh was failing or whether the
// fixture-side data was missing. Errors now:
//   - print to console.error with the RPC error shape preserved
//   - render a visible banner above the dashboard so the operator
//     sees the failure on the page itself
// The dashboard still mounts — empty snapshots render "—" honestly,
// the same as before. The banner is additive, not blocking.

import { useEffect, useState } from "react";

import { AdminDashboard } from "../../modes/dashboard/AdminDashboard.tsx";
import { getBrowserClient } from "../../supabase/browser.ts";

interface RefreshError {
  readonly message: string;
  readonly code: string | null;
}

export function AdminDashboardPage(): JSX.Element {
  const [refreshed, setRefreshed] = useState(false);
  const [refreshError, setRefreshError] = useState<RefreshError | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let err: RefreshError | null = null;
      try {
        const client = getBrowserClient();
        const resp = await (client.rpc as unknown as (
          fn: string,
        ) => Promise<{ data: unknown; error: { message?: string; code?: string } | null }>)(
          "refresh_admin_dashboard",
        );
        if (resp.error) {
          err = {
            message: resp.error.message ?? "refresh_admin_dashboard returned an error",
            code: resp.error.code ?? null,
          };
        }
      } catch (caught: unknown) {
        err = {
          message: caught instanceof Error ? caught.message : String(caught),
          code: null,
        };
      } finally {
        if (!cancelled) {
          if (err) {
            // eslint-disable-next-line no-console
            console.error("[AdminDashboardPage] refresh_admin_dashboard failed:", err);
            setRefreshError(err);
          }
          setRefreshed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Mount the dashboard after the refresh attempt so the widget hook
  // reads fresh snapshots (it fetches once on mount).
  if (!refreshed) {
    return (
      <div
        data-testid="admin-dashboard-refreshing"
        style={{
          padding: "48px 28px",
          fontFamily: "var(--se-font-mono, monospace)",
          fontSize: 12,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "var(--se-text-muted, #888)",
        }}
      >
        Refreshing metrics…
      </div>
    );
  }
  return (
    <>
      {refreshError && (
        <div
          data-testid="admin-dashboard-refresh-error"
          role="alert"
          style={{
            margin: "12px 28px 0",
            padding: "10px 14px",
            border: "1px solid var(--se-danger, #c0392b)",
            borderRadius: 6,
            background: "rgba(192, 57, 43, 0.08)",
            fontFamily: "var(--se-font-mono, monospace)",
            fontSize: 12,
            color: "var(--se-danger, #c0392b)",
          }}
        >
          <strong>Dashboard refresh failed.</strong>{" "}
          Snapshots may be stale or absent. Error:{" "}
          {refreshError.code ? `${refreshError.code} — ` : ""}
          {refreshError.message}
        </div>
      )}
      <AdminDashboard />
    </>
  );
}
